import React, { useEffect, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { DataGrid } from '@mui/x-data-grid';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import StatCard from '../../components/ui/StatCard';
import { PaymentStatusChip } from '../../components/ui/StatusChip';
import api from '../../api';
import { formatCurrency } from '../../utils/image';

// Acompanhamento das cobranças PIX (mensalidades) e do retorno dos webhooks.
const PaymentsAdmin = ({ onMessage }) => {
  const [payments, setPayments] = useState([]);
  const [status, setStatus] = useState('');
  const [referencia, setReferencia] = useState('');
  const [loading, setLoading] = useState(false);
  const [sincronizando, setSincronizando] = useState(null); // id do pagamento ou 'todos'
  const [ultimaSincronizacao, setUltimaSincronizacao] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get('pagamentos', { params: { status: status || undefined, referencia: referencia || undefined } });
      setPayments(response.data.payments.map((p) => ({ ...p, id: p._id })));
      setUltimaSincronizacao(response.data.ultimaSincronizacao);
    } catch (error) {
      onMessage && onMessage('Erro ao carregar pagamentos', 'error');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, referencia]);

  const resumo = useMemo(() => {
    const soma = (lista) => lista.reduce((t, p) => t + p.amount, 0);
    const pendentes = payments.filter((p) => p.status === 'pending');
    const pagos = payments.filter((p) => p.status === 'paid');
    return { pendentes: pendentes.length, valorPendente: soma(pendentes), pagos: pagos.length, valorPago: soma(pagos) };
  }, [payments]);

  const referencias = useMemo(() => [...new Set(payments.map((p) => p.referencia).filter(Boolean))].sort().reverse(), [payments]);

  // Consulta a operadora (todas as tentativas de PIX) e atualiza o status do pagamento.
  const sincronizar = async (payment) => {
    setSincronizando(payment._id);
    try {
      const response = await api.post(`pagamentos/${payment._id}/sincronizar`);
      onMessage && onMessage(response.data.mensagem, response.data.alterado ? 'success' : 'info');
      if (response.data.alterado) await load();
    } catch (error) {
      onMessage && onMessage(error.response?.data?.error || 'Erro ao sincronizar', 'error');
    }
    setSincronizando(null);
  };

  const sincronizarPendentes = async () => {
    setSincronizando('todos');
    try {
      const { data } = await api.post('pagamentos/sincronizar-pendentes');
      const r = data.resumo;
      onMessage && onMessage(`${r.verificados} verificados · ${r.pagos} confirmados como pagos${r.falhas ? ` · ${r.falhas} com falha na consulta` : ''}`, r.falhas ? 'warning' : 'success');
      await load();
    } catch (error) {
      onMessage && onMessage(error.response?.data?.error || 'Erro ao sincronizar', 'error');
    }
    setSincronizando(null);
  };

  const copyLink = async (token) => {
    await navigator.clipboard.writeText(`${window.location.origin}/pagamento/${token}`);
    onMessage && onMessage('Link de pagamento copiado.', 'success');
  };

  const columns = [
    { field: 'user', headerName: 'Jogador', flex: 1, minWidth: 180, valueGetter: (params) => params.row.user?.nome || '-' },
    { field: 'description', headerName: 'Descrição', flex: 1.2, minWidth: 220 },
    { field: 'amount', headerName: 'Valor', width: 110, valueFormatter: (params) => formatCurrency(params.value) },
    { field: 'status', headerName: 'Status', width: 120, renderCell: (params) => <PaymentStatusChip status={params.value} /> },
    { field: 'provider', headerName: 'Provedor', width: 120, valueFormatter: (params) => (params.value === 'mercadopago' ? 'Mercado Pago' : params.value === 'asaas' ? 'Asaas' : '-') },
    { field: 'paidAt', headerName: 'Pago em', width: 160, valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString('pt-BR') : '-') },
    {
      field: 'acoes',
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: (params) => {
        const podeSincronizar = params.row.status !== 'paid' && params.row.status !== 'refunded' && (params.row.attempts || []).length > 0;
        return (
          <>
            {podeSincronizar && (
              <Tooltip title="Sincronizar com a operadora">
                <span>
                  <IconButton size="small" onClick={() => sincronizar(params.row)} disabled={Boolean(sincronizando)}>
                    {sincronizando === params.row._id ? <CircularProgress size={16} /> : <SyncRoundedIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {params.row.status === 'pending' && (
              <Tooltip title="Copiar link de pagamento">
                <IconButton size="small" onClick={() => copyLink(params.row.token)}><ContentCopyRoundedIcon fontSize="small" /></IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}><StatCard label="Pendentes" value={resumo.pendentes} hint={formatCurrency(resumo.valorPendente)} icon={<HourglassTopRoundedIcon />} tone="gold" /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Pagos" value={resumo.pagos} hint={formatCurrency(resumo.valorPago)} icon={<PaidRoundedIcon />} tone="success" /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Cobranças listadas" value={payments.length} icon={<ReceiptLongRoundedIcon />} tone="neutral" /></Grid>
      </Grid>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="pending">Pendente</MenuItem>
          <MenuItem value="paid">Pago</MenuItem>
          <MenuItem value="cancelled">Cancelado</MenuItem>
          <MenuItem value="refunded">Estornado</MenuItem>
        </TextField>
        <TextField select size="small" label="Referência" value={referencia} onChange={(e) => setReferencia(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">Todas</MenuItem>
          {referencias.map((ref) => <MenuItem key={ref} value={ref}>{ref}</MenuItem>)}
        </TextField>
        <Tooltip title="Atualizar lista">
          <IconButton onClick={load}><RefreshRoundedIcon /></IconButton>
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" startIcon={sincronizando === 'todos' ? <CircularProgress size={16} /> : <SyncRoundedIcon />} onClick={sincronizarPendentes} disabled={Boolean(sincronizando)}>
          Sincronizar pendentes
        </Button>
      </Stack>
      {ultimaSincronizacao && (
        <Typography variant="caption" color="text.secondary">
          Última sincronização com a operadora: {new Date(ultimaSincronizacao.em).toLocaleString('pt-BR')} ({ultimaSincronizacao.origem}
          {ultimaSincronizacao.referencia ? `, pagamentos de ${ultimaSincronizacao.referencia}` : ''}) · {ultimaSincronizacao.verificados} verificados, {ultimaSincronizacao.pagos} confirmados como pagos
          {ultimaSincronizacao.falhas ? `, ${ultimaSincronizacao.falhas} com falha` : ''}. A sincronização automática roda todo dia às 7h.
        </Typography>
      )}
      <DataGrid rows={payments} columns={columns} autoHeight loading={loading} disableRowSelectionOnClick initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} pageSizeOptions={[10, 25, 50]} />
    </Stack>
  );
};

export default PaymentsAdmin;
